// Small styled primitives shared across screens. Inline styles (as the design uses),
// driven by the token CSS vars in global.css.
import type { CSSProperties, ReactNode } from 'react';

export const card: CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: 'var(--card-shadow)',
};

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
  full,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  full?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? '#CBD5D8' : 'var(--teal)',
        color: '#fff',
        fontSize: 16,
        fontWeight: 800,
        padding: '15px 30px',
        borderRadius: 10,
        boxShadow: disabled ? 'none' : 'var(--teal-shadow)',
        width: full ? '100%' : undefined,
        transition: 'background .15s ease, opacity .15s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// The teal checkmark badge shown on a selected option.
export function CheckBadge() {
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <circle cx={10} cy={10} r={10} fill="var(--teal)" />
      <path d="M5.8 10.2l2.8 2.8 5.6-5.8" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The empty radio dot shown on an unselected option.
export function EmptyDot() {
  return <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--option-empty)', flexShrink: 0 }} />;
}

// A tappable single-select row. Selected → teal-tint surface + check badge.
export function SelectRow({
  label,
  sublabel,
  selected,
  onSelect,
  leading,
}: {
  label: string;
  sublabel?: string;
  selected: boolean;
  onSelect: () => void;
  leading?: ReactNode;
}) {
  return (
    <button
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        textAlign: 'left',
        width: '100%',
        background: selected ? 'var(--teal-tint)' : '#fff',
        borderRadius: 12,
        padding: '17px 20px',
        boxShadow: selected ? '0 4px 24px rgba(33,172,190,.22)' : 'var(--card-shadow)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {leading}
        <span>
          <span
            style={{
              display: 'block',
              fontSize: 16,
              fontWeight: selected ? 800 : 700,
              color: selected ? 'var(--teal-deep)' : 'var(--ink)',
            }}
          >
            {label}
          </span>
          {sublabel && (
            <span
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                marginTop: 3,
                color: selected ? 'var(--teal-deep)' : 'var(--slate)',
                opacity: selected ? 0.85 : 1,
              }}
            >
              {sublabel}
            </span>
          )}
        </span>
      </span>
      {selected ? <CheckBadge /> : <EmptyDot />}
    </button>
  );
}
